import React, { useState, useRef } from 'react';
import { X, Mic, Square, Sparkles, Trophy, RotateCcw, Film } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

/**
 * MovieGuessGame: voice (or text) mini-game.
 * The user listens to a clue (synopsis with title censored) and tries to guess the movie.
 */
const MovieGuessGame = ({ onClose }) => {
  const { t } = useLanguage();
  const [phase, setPhase] = useState('idle'); // idle | playing | finished
  const [session, setSession] = useState(null);
  const [clue, setClue] = useState(null);
  const [textAnswer, setTextAnswer] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]); // [{transcribed, is_correct, similarity}]
  const [result, setResult] = useState(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startGame = async () => {
    try {
      const res = await axios.post('/api/movie-game/start');
      setSession(res.data.session_id);
      setClue(res.data.clue);
      setPhase('playing');
      setHistory([]);
      setResult(null);
    } catch (e) {
      toast.error('Impossible de démarrer la partie');
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && isRecording) {
      mediaRecRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mimeType = '';
      for (const f of ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']) {
        if (MediaRecorder.isTypeSupported(f)) { mimeType = f; break; }
      }
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        await submitAnswer(null, blob);
      };
      mr.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(p => {
          if (p >= 8) { stopRecording(); return 8; }
          return p + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Impossible d'accéder au micro");
    }
  };

  const submitAnswer = async (text, audioBlob) => {
    if (!session || submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('session_id', session);
      if (text) fd.append('text_answer', text);
      if (audioBlob) {
        const ext = (audioBlob.type || '').includes('mp4') ? 'mp4' : 'webm';
        fd.append('audio', new File([audioBlob], `answer.${ext}`, { type: audioBlob.type }));
      }
      const res = await axios.post('/api/movie-game/answer', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = res.data;
      setHistory(h => [...h, r]);
      setTextAnswer('');
      if (r.finished) {
        setResult(r);
        setPhase('finished');
      } else if (r.is_correct) {
        // edge: correct on same call (handled by finished)
      } else {
        toast.message(`Pas tout à fait... essaie encore ! (${r.attempts}/${r.max_attempts})`);
      }
    } catch (e) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitText = (e) => {
    e.preventDefault();
    if (!textAnswer.trim()) return;
    submitAnswer(textAnswer.trim(), null);
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-center justify-center p-4" data-testid="movie-game-modal">
      <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Film className="w-5 h-5 text-magenta-400" />
            <h2 className="text-lg font-bold">{t('filmIA.movieGameTitle').replace('🎬 ', '').replace(/^[A-Za-zÀ-ÿ\s]+:\s*/, '')}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-movie-game">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {phase === 'idle' && (
            <div className="text-center space-y-4 py-6">
              <Sparkles className="w-12 h-12 text-yellow-400 mx-auto" />
              <h3 className="text-xl font-bold">{t('filmIA.movieGameReady')}</h3>
              <p className="text-sm text-gray-400">
                {t('filmIA.movieGameDescription')}
              </p>
              <button
                onClick={startGame}
                className="w-full py-3 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-2xl font-bold hover:from-magenta-600 hover:to-violet-700 shadow-lg shadow-magenta-500/30 transition-all"
                data-testid="movie-game-start"
              >
                {t('filmIA.movieGameStart')}
              </button>
            </div>
          )}

          {phase === 'playing' && clue && (
            <>
              <div className="bg-white/5 rounded-2xl p-4 space-y-2 border border-white/10">
                <div className="flex items-start gap-3">
                  {clue.poster_url && (
                    <img src={clue.poster_url} alt="" className="w-20 h-28 object-cover rounded-lg blur-sm hover:blur-none transition-all" />
                  )}
                  <div className="flex-1 text-sm">
                    {clue.year && <p className="text-gray-400 text-xs mb-1">📅 {clue.year}</p>}
                    {clue.director && <p className="text-gray-300 text-xs mb-1">🎬 {clue.director}</p>}
                    {clue.cast?.length > 0 && <p className="text-gray-300 text-xs mb-1">🌟 {clue.cast.join(', ')}</p>}
                    {clue.vote_average && <p className="text-yellow-400 text-xs">⭐ {clue.vote_average}/10</p>}
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-gray-200" data-testid="movie-game-synopsis">
                  {clue.synopsis}
                </p>
              </div>

              {history.length > 0 && (
                <div className="space-y-1.5">
                  {history.map((h, i) => (
                    <div key={i} className={`text-xs px-3 py-2 rounded-xl border ${
                      h.is_correct ? 'bg-green-500/10 border-green-500/40 text-green-300' : 'bg-red-500/10 border-red-500/40 text-red-300'
                    }`}>
                      {t('filmIA.movieGameAttempt')} {i + 1} : « {h.transcribed} » — {h.is_correct ? '✅' : `❌ ${Math.round(h.similarity * 100)}%`}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={handleSubmitText} className="flex gap-2">
                <input
                  type="text"
                  value={textAnswer}
                  onChange={(e) => setTextAnswer(e.target.value)}
                  placeholder={t('filmIA.movieGameAnswerPlaceholder')}
                  className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
                  disabled={submitting || isRecording}
                  data-testid="movie-game-text-input"
                />
                <button
                  type="submit"
                  disabled={!textAnswer.trim() || submitting || isRecording}
                  className="px-4 py-3 bg-magenta-500 hover:bg-magenta-600 rounded-2xl disabled:opacity-50 font-medium"
                  data-testid="movie-game-submit-text"
                >
                  {t('filmIA.movieGameSubmit')}
                </button>
              </form>

              <div className="flex items-center justify-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-gray-500">{t('filmIA.movieGameOrVoice')}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <button
                type="button"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={submitting}
                className={`w-full py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                  isRecording
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                    : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'
                } disabled:opacity-50`}
                data-testid="movie-game-voice-btn"
              >
                {isRecording ? (
                  <>
                    <Square className="w-5 h-5" /> {t('filmIA.movieGameStop')} ({recordingTime}s / 8s)
                  </>
                ) : (
                  <>
                    <Mic className="w-5 h-5" /> {t('filmIA.movieGameVoiceAnswer')}
                  </>
                )}
              </button>
            </>
          )}

          {phase === 'finished' && result && (
            <div className="text-center py-6 space-y-4" data-testid="movie-game-result">
              {result.is_correct ? (
                <>
                  <Trophy className="w-16 h-16 text-yellow-400 mx-auto animate-bounce" />
                  <h3 className="text-2xl font-bold text-yellow-300">{t('filmIA.movieGameBravo')}</h3>
                  <p className="text-4xl font-extrabold bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">
                    +{result.score} pts
                  </p>
                </>
              ) : (
                <>
                  <Film className="w-16 h-16 text-gray-500 mx-auto" />
                  <h3 className="text-xl font-bold">{t('filmIA.movieGameTryAgain')}</h3>
                </>
              )}
              <p className="text-sm text-gray-400">
                {t('filmIA.movieGameCorrectAnswer')} <span className="text-white font-bold">{result.correct_title}</span>
              </p>
              <button
                onClick={startGame}
                className="w-full py-3 bg-gradient-to-r from-magenta-500 to-violet-600 rounded-2xl font-bold flex items-center justify-center gap-2"
                data-testid="movie-game-replay"
              >
                <RotateCcw className="w-4 h-4" /> {t('filmIA.movieGameReplay')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MovieGuessGame;
